import { Component, OnInit, inject } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Data, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { LoginService } from '@uoa/auth';
import { Prompt } from '../../../domain/interfaces/prompts';
import { of } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { AudioPlayerComponent } from '../audio-player/audio-player.component';
import { IonicModule } from '@ionic/angular';

@Component({
  selector: 'app-prompt-list',
  templateUrl: './prompt-list.component.html',
  styleUrls: ['./prompt-list.component.scss'],
  imports: [FormsModule, AudioPlayerComponent, IonicModule, RouterLink, RouterLinkActive],
})
export class PromptListComponent implements OnInit {
  private httpClient = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private loginService = inject(LoginService);

  public contactFlowList: string[];
  public contactFlowFilter = 'All';

  public keywordList: string[];
  public keywordFilter = 'All';

  public promptList: Prompt[] = [];
  public api: Data;

  ngOnInit() {
    this.route.data.subscribe((api) => {
      console.log('Got API: ', api);
      this.api = api;

      if (environment.wallboards.prompt.useMockData) {
        console.log('mock data');

        // Set the contact flow list and filter out duplicates
        this.contactFlowList = api.prompts.mockData.data.Items.map((prompt) => prompt.flow_name).filter(
          (value, index, self) => self.indexOf(value) === index,
        );

        // Add a blank item to start the list
        this.contactFlowList.unshift('All');

        // Set the keyword list and filter out duplicates
        this.keywordList = api.prompts.mockData.data.Items.map((prompt) => prompt.keyword).filter(
          (value, index, self) => self.indexOf(value) === index,
        );

        // Add a blank item to start the list
        this.keywordList.unshift('All');

        // Set the prompt list
        this.promptList = environment.wallboards.prompt.prompts.mockData.data.Items as unknown as Prompt[];
      } else {
        console.log('real data');

        of(null)
          .pipe(
            tap(async () => {
              const isAuthed = await this.loginService.isAuthenticated();
              // get user info
              const user = await this.loginService.getUserInfo();
              console.log(user);
              if (!isAuthed) {
                await this.loginService.doLogin(environment.auth.redirectUri + this.router.url);
              }
            }),
            // Get the prompts
            mergeMap(() =>
              this.httpClient.get<Prompt[]>(api.prompts.baseUrl).pipe(
                tap((data) => {
                  console.log('data', data);
                }),
                tap((prompts) => {
                  const normalizedPrompts = prompts.map((prompt) => this.normalizePrompt(prompt));

                  // Set the contact flow list and filter out duplicates
                  this.contactFlowList = normalizedPrompts.map((item) => item.flow_name);
                  this.keywordList = normalizedPrompts.map((item) => item.keyword);

                  // Add a blank item to start the list
                  this.contactFlowList.unshift('All');

                  // Filter out duplicates
                  this.contactFlowList = this.contactFlowList.filter((item, index) => this.contactFlowList.indexOf(item) === index);

                  // Filter out any prompts flows that start with "Default" or "Sample" or "ZZ"
                  this.contactFlowList = this.contactFlowList.filter(
                    (item) => !item.startsWith('Default') && !item.startsWith('Sample') && !item.startsWith('ZZ'),
                  );

                  // Add a blank item to start the list
                  this.keywordList.unshift('All');

                  // Fillter out duplicate keywords
                  this.keywordList = this.keywordList.filter((item, index) => this.keywordList.indexOf(item) === index);

                  // Set the prompt list
                  this.promptList = normalizedPrompts;
                }),
                catchError((err) => {
                  console.log('Error getting prompts: ', err);
                  return of([]);
                }),
              ),
            ),
            catchError((err) => {
              console.log('Error getting prompt', err);
              return of(null);
            }),
          )
          .subscribe({
            next: () => {
              console.log('prompt list', this.promptList);
            },
          });
      }
    });
  }

  computedPromptList() {
    let filteredPromptList = this.promptList;

    if (this.contactFlowFilter !== 'All') {
      filteredPromptList = filteredPromptList.filter((prompt) => {
        return prompt.flow_name === this.contactFlowFilter;
      });
    }

    if (this.keywordFilter !== 'All') {
      // Filter the prompt list by keyword
      filteredPromptList = filteredPromptList.filter((prompt) => {
        return prompt.keyword === this.keywordFilter;
      });
    }

    return filteredPromptList;
  }

  isPromptActive(prompt: Prompt) {
    const start_time = this.normalizeTimestampValue(prompt.start_time);
    const end_time = this.normalizeTimestampValue(prompt.end_time);

    if (start_time === '' && end_time === '') {
      return true;
    }

    const nowMs = Date.now();
    const startMs = this.toTimestampMs(start_time);
    const endMs = this.toTimestampMs(end_time);

    // If end time is disabled and start time is before current time, return true
    if (start_time === '' && endMs !== null && nowMs < endMs) {
      return true;
    }

    // If start time is disabled and end time is after current time, return true
    if (end_time === '' && startMs !== null && nowMs > startMs) {
      return true;
    }

    return startMs !== null && endMs !== null && nowMs > startMs && nowMs < endMs;
  }

  getFlowList() {
    // Check if the keyword filter is set to all
    if (this.keywordFilter !== 'All') {
      // Filter the flow list by keyword
      const list = this.contactFlowList.filter((flow) => {
        return this.promptList.find((prompt) => {
          return prompt.flow_name === flow && prompt.keyword === this.keywordFilter;
        });
      });

      // Add 'All' to start
      list.unshift('All');

      return list;
    }

    return this.contactFlowList;
  }

  getKeywordList() {
    // Check if the contact flow has been set
    if (this.contactFlowFilter !== 'All') {
      // Filter the keyword list by contact flow
      const list = this.keywordList.filter((keyword) => {
        return (
          this.promptList.filter((prompt) => {
            return prompt.keyword === keyword && prompt.flow_name === this.contactFlowFilter;
          }).length > 0
        );
      });

      // Add 'All' to start
      list.unshift('All');

      return list;
    }
    return this.keywordList;
  }

  formatPrompt(text: string) {
    // Remove <speak> tags
    return text.replace('<speak>', '').replace('</speak>', '');
  }

  getDate(date: string | number) {
    const normalizedDate = this.normalizeTimestampValue(date);
    return typeof normalizedDate === 'number' ? this.formatDisplayDate(normalizedDate) : '';
  }

  private toTimestampMs(value: string | number): number | null {
    if (value === '' || value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'number') {
      return this.normalizeEpochToMs(value);
    }

    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return this.normalizeEpochToMs(parsed);
    }

    const dateMs = new Date(value).getTime();
    return Number.isNaN(dateMs) ? null : dateMs;
  }

  private formatDisplayDate(value: number): string {
    const date = new Date(value);
    const hours24 = date.getHours();
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    const meridiem = hours24 >= 12 ? 'pm' : 'am';
    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();

    return `${`${hours12}`.padStart(2, '0')}:${minutes}${meridiem} ${day}/${month}/${year}`;
  }

  private normalizePrompt(prompt: Prompt): Prompt {
    return {
      ...prompt,
      start_time: this.normalizeTimestampValue(prompt.start_time),
      end_time: this.normalizeTimestampValue(prompt.end_time),
    };
  }

  private normalizeTimestampValue(value: string | number): string | number {
    const timestampMs = this.toTimestampMs(value);
    return timestampMs === null || timestampMs === 0 ? '' : timestampMs;
  }

  private normalizeEpochToMs(value: number): number {
    return Math.abs(value) < 1_000_000_000_000 ? value * 1000 : value;
  }
}
